import React, { useEffect, useState, useRef } from "react";
import "leaflet/dist/leaflet.css";
import "leaflet-geometryutil";
import { LatLng } from "leaflet";
import { MapContainer, TileLayer, Polyline } from "react-leaflet";
import { useTranslation } from "react-i18next";
import polyline from "@mapbox/polyline";
import L from "leaflet";
import { MarkerType, QuizContentProps } from "../../../../types/screensTypes";
import { deleteQuizAPI, getGeocode, saveQuiz, getQuizForTrack } from "../../../../api";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import CustomMarker from "../../../../components/map/MapMarker";
import TextInput from "../../../../components/ui/TextInput";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import { useGlobalState } from "../../../../context/useGlobalState";
import { FieldErrorsQuiz, Quiz } from "../../../../types/types";
import Button from "../../../../components/ui/Button";
import QuizList from "../../../../components/lists/QuizList";
import { MapInitializer } from "../../../../utils/mapInitializer";
import shell from "../createShell.module.css";
import mapStyles from "../createMap.module.css";
import stepStyles from "../createStep.module.css";

export const QuizContent: React.FC<QuizContentProps> = ({ navigateToNext, navigateToContinue }) => {
    const { trackData } = useTrackCreation();
    const { data } = useGlobalState();
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const [address] = useState<string>("");
    const [coordinates, setCoordinates] = useState<string>("");
    const [position, setPosition] = useState<string>(""); // using string for TextInput
    const [wptType] = useState<string | null>(null);
    const [quizzes, setQuizzes] = useState<Quiz[]>([]);
    const [cityName, setCityName] = useState<string | null>(null);
    const [map, setMap] = useState<L.Map | null>(null);
    const [loadingSave, setLoadingSave] = useState<boolean>(false);
    const [, setIsGenerateButtonDisabled] = useState<boolean>(true);
    const [showErrors, setShowErrors] = useState<boolean>(false);
    const [polylineDecoded, setPolylineDecoded] = useState<L.LatLng[] | null>(null);
    const hasExecuted = useRef<boolean>(false);

    const deleteWaypoint = async (id: string): Promise<void> => {
        setLoadingSave(true);
        const trackId = trackData.trackId;
        const token = localStorage.getItem("token");
        if (!token || !trackId) {
            setLoadingSave(false);
            return;
        }
        try {
            await deleteQuizAPI({ token, trackId, quizId: id });
            await getQuizTrackById(trackId);
            setLoadingSave(false);
        } catch (err) {
            setLoadingSave(false);
        }
    };

    const handleAddWaypoint = async (): Promise<void> => {
        let latitude: string | undefined;
        let longitude: string | undefined;
        const token = localStorage.getItem("token") || "";
        const trackId = trackData.trackId;

        if (!trackId || !token) return;

        if (coordinates.trim() !== "") {
            const parts = coordinates.split(",");
            if (parts.length >= 2) {
                latitude = parts[0];
                longitude = parts[1];
            }
        } else if (address.trim() !== "") {
            const geocodeResult = await getGeocode({
                address: address + " " + (cityName || ""),
            });
            if (geocodeResult) {
                latitude = String(geocodeResult.lat);
                longitude = String(geocodeResult.lon);
            } else {
                window.alert(t("addressNotFound"));
            }
        }

        const posNumber = parseInt(position);
        const existingWaypointWithSamePosition = quizzes.find((quiz) => quiz.position === posNumber);

        if (existingWaypointWithSamePosition) {
            const allWaypointsPositionToChange = quizzes.filter((quiz) => quiz.position >= posNumber);

            allWaypointsPositionToChange.forEach((quiz, index) => {
                const newPosition = posNumber + index + 1; // New position
                if (quiz.position !== newPosition) {
                    // Save updated quiz position (fire and forget)
                    saveQuiz({
                        token,
                        trackId,
                        quizId: quiz.id,
                        latitude: quiz.point.lat,
                        longitude: quiz.point.lng,
                        position: newPosition,
                    });
                }
            });
        }

        if (latitude && longitude && position) {
            const token = localStorage.getItem("token") || "";
            // Save new quiz waypoint

            await saveQuiz({
                token,
                trackId,
                quizId: null,
                latitude: parseFloat(latitude.trim()),
                longitude: parseFloat(longitude.trim()),
                address,
                position: parseInt(position),
            });

            await getQuizTrackById(trackId);
        } else {
            setShowErrors(true);
            setTimeout(() => {
                setShowErrors(false);
            }, 3000);
            return;
        }
    };

    const handleMarkerDragEnd = async (newPosition: LatLng, id: string): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!token || !trackId) return;
        const { lat, lng } = newPosition;

        await saveQuiz({
            token,
            trackId,
            quizId: id,
            latitude: lat,
            longitude: lng,
        });
        await getQuizTrackById(trackId);
    };

    const getQuizTrackById = async (id: string): Promise<void> => {
        const token = localStorage.getItem("token");

        if (!token) return;

        const responseData = await getQuizForTrack({ token, trackId: id });
        if (responseData) {
            const wpts: Quiz[] = responseData.quizzes;
            if (responseData.polyline && responseData.polyline !== "") {
                const decodedPolyline = polyline.decode(responseData.polyline);
                const polylineLatLngs = decodedPolyline.map(([lat, lng]: [number, number]) => L.latLng(lat, lng));
                setPolylineDecoded(polylineLatLngs);
            }
            // Ensure each quiz has type "QUIZ"
            wpts.forEach((element) => {
                element.type = "QUIZ";
            });
            setQuizzes(wpts);
        }
    };

    useEffect(() => {
        if (trackData && trackData.trackId) {
            getQuizTrackById(trackData.trackId);
        }
    }, [trackData.trackId]);

    useEffect(() => {
        if (!hasExecuted.current && map) {
            if (quizzes && quizzes.length > 0) {
                const latLngBounds = L.latLngBounds([]);
                quizzes.forEach((waypoint) => {
                    latLngBounds.extend([waypoint.point?.lat ?? 0, waypoint.point?.lng ?? 0]);
                });
                map.fitBounds(latLngBounds);
                hasExecuted.current = true;
            }
        }
    }, [map, quizzes]);

    useEffect(() => {
        const cities = data?.locationCityOptions || [];
        const trackCity = trackData?.city;
        const cityFilter = cities.filter((city: { value: string; label: string }) => city.value === trackCity);
        const city = cityFilter.length > 0 ? cityFilter[0] : null;
        if (city) {
            setCityName(city.label);
        }
    }, [data, trackData]);

    const setMapCenterOnReady = (mapInstance: L.Map | null) => {
        if (!mapInstance) {
            return;
        }
        setMap(mapInstance);
    };

    const [fieldErrors, setFieldErrors] = useState<FieldErrorsQuiz>({
        address: false,
        coordinates: false,
        position: false,
        wptType: false,
    });

    useEffect(() => {
        const errors: FieldErrorsQuiz = {
            address: !address && !coordinates, // No error if either address or coordinates is present
            coordinates: !coordinates && !address,
            position: !position,
            wptType: !wptType,
        };
        setFieldErrors(errors);
        const hasErrors = Object.values(errors).some((error) => error);
        setIsGenerateButtonDisabled(hasErrors);
    }, [address, coordinates, position, wptType]);

    const handleMapReady = (mapInstance: L.Map) => {
        setMap(mapInstance);
    };

    return (
        <div className={`content-container experience-map ${shell.mapShell} ${mapStyles.quizShell}`}>
            <div className={mapStyles.leftColumn}>
                <div className={`page-title experience-map-title ${mapStyles.titleBlock}`}>
                    <TitleSubtitle title={t("quiz")} subtitle={t("adjustTheQuizOfYourAudioTourHere")} layout="map" />
                </div>

                <div className={mapStyles.mapContainer}>
                    <div className="map-placeholder">
                        <MapContainer
                            center={[1, 1]}
                            zoom={16}
                            style={{ height: "480px", width: "100%" }}
                            scrollWheelZoom={false}
                            doubleClickZoom={false}
                            whenReady={() => setMapCenterOnReady(map)}
                        >
                            <MapInitializer onMapReady={handleMapReady} />
                            <TileLayer
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            />
                            {polylineDecoded && (
                                <Polyline
                                    positions={polylineDecoded}
                                    pathOptions={{ color: "#1930d8", weight: 4, opacity: 0.85 }}
                                />
                            )}

                            {quizzes?.map((waypoint) => {
                                if (!waypoint?.point?.lat || !waypoint?.point?.lng) return null;

                                const markerType = [
                                    "GARDEN",
                                    "INVISIBLE",
                                    "MOBILE",
                                    "FIXED",
                                    "QUIZ",
                                    "SELECTED",
                                    "STOP",
                                ].includes(waypoint.type.toString())
                                    ? (waypoint.type as MarkerType)
                                    : undefined;

                                return (
                                    <CustomMarker
                                        key={waypoint.id}
                                        id={waypoint.id}
                                        lat={waypoint.point.lat}
                                        lng={waypoint.point.lng}
                                        type={markerType}
                                        position={waypoint.position}
                                        onSelect={() => navigateToNext(waypoint.id)}
                                        onMarkerDragEnd={handleMarkerDragEnd}
                                    />
                                );
                            })}
                        </MapContainer>
                    </div>
                </div>

                <div className={`experience-map-add ${mapStyles.add}`}>
                    <h3 className={`experience-map-add-title ${mapStyles.addTitle}`}>{t("addANewWaypoint")}</h3>
                    <div className={`add-waypoint-form experience-map-add-fields ${mapStyles.addFields}`}>
                        <div className="add-waypoint-coordinates">
                            <TextInput
                                placeholder={t("enterCoordinatesLatLng")}
                                value={coordinates}
                                onChange={(e) => setCoordinates(e.target.value)}
                                isMissing={showErrors && fieldErrors.coordinates}
                            />
                        </div>
                        <TextInput
                            placeholder={t("enterPosition")}
                            value={position}
                            onChange={(e) => setPosition(e.target.value)}
                            isMissing={showErrors && fieldErrors.position}
                        />
                        <Button
                            label={t("add")}
                            onClick={handleAddWaypoint}
                            variant="primary"
                            shape="pill"
                            className={`${stepStyles.continueBtn} ${mapStyles.mapBtn}`}
                        />
                    </div>
                </div>
            </div>

            <div className={`right-sidebar experience-map-poi ${mapStyles.poiColumn}`}>
                <div className="waypoints-list">
                    <div className={`waypoints-list-header ${mapStyles.waypointsHeader}`}>
                        <h3>{t("waypoints")}</h3>
                    </div>
                    <QuizList
                        quizes={quizzes}
                        setQuizes={setQuizzes}
                        deleteQuiz={(id: string) => deleteWaypoint(id)}
                        navigateToNext={navigateToNext}
                        isLoading={loadingSave}
                        saveQuiz={saveQuiz}
                    />
                </div>
            </div>

            {navigateToContinue && (
                <div className={`${stepStyles.stepButton} ${stepStyles.footer} ${stepStyles.footerMap}`}>
                    <Button
                        label={t("continue")}
                        onClick={navigateToContinue}
                        variant="primary"
                        shape="pill"
                        className={`${stepStyles.continueBtn} ${mapStyles.mapBtn}`}
                    />
                </div>
            )}
        </div>
    );
};

export default QuizContent;
